import { z } from "zod";

// ── Response schemas (read sides of /api/claim-regions, /api/claim-teams, etc.) ──

export const claimRegionSchema = z.object({
  id: z.string(),
  claimRegion: z.string(),
  sortOrder: z.number(),
  isManagementGroup: z.boolean(),
  isBackupHistory: z.boolean(),
});
export const claimRegionArraySchema = z.array(claimRegionSchema);
export type ClaimRegion = z.infer<typeof claimRegionSchema>;

export const claimTeamSchema = z.object({
  id: z.string(),
  claimRegionTeam: z.string(),
  manager: z.string(),
  sortOrder: z.number(),
  isClaimTeam: z.boolean(),
  claimRegionId: z.string(),
});
export const claimTeamArraySchema = z.array(claimTeamSchema);
export type ClaimTeam = z.infer<typeof claimTeamSchema>;

export const teamMemberSchema = z.object({
  id: z.string(),
  userId: z.string(),
  isExcludeFromPdf: z.boolean(),
  firstLastName: z.string().nullable(),
  title: z.string().nullable(),
  email: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  extension: z.string().nullable(),
  mobilePhone: z.string().nullable(),
  location: z.string().nullable(),
});
export const teamMemberArraySchema = z.array(teamMemberSchema);
export type TeamMember = z.infer<typeof teamMemberSchema>;

export const periodSchema = z.object({
  id: z.string(),
  name: z.string(),
  isLocked: z.boolean(),
  createdAt: z.string(),
});
export const periodArraySchema = z.array(periodSchema);
export type Period = z.infer<typeof periodSchema>;

// History-page filter dropdown rows. Returned as plain `{ field }` shapes
// from /api/claim-team-history/regions and /teams (period-scoped). Kept as
// thin objects rather than collapsing to z.array(z.string()) so the API can
// add columns later without breaking the contract.
export const regionRowSchema = z.object({ claimRegion: z.string() });
export const regionRowArraySchema = z.array(regionRowSchema);
export type RegionRow = z.infer<typeof regionRowSchema>;

export const teamRowSchema = z.object({ claimRegionTeam: z.string() });
export const teamRowArraySchema = z.array(teamRowSchema);
export type TeamRow = z.infer<typeof teamRowSchema>;

export const rosterRowSchema = z.object({
  id: z.string(),
  claimRegion: z.string(),
  claimRegionTeam: z.string(),
  userId: z.string(),
  title: z.string(),
  isActive: z.boolean(),
});
export const rosterRowArraySchema = z.array(rosterRowSchema);
export type RosterRow = z.infer<typeof rosterRowSchema>;

export const unboundRecordSchema = z.object({
  id: z.string(),
  claimRegion: z.string(),
  claimRegionTeam: z.string(),
  userId: z.string(),
  title: z.string(),
  isActive: z.boolean(),
});
export const unboundRecordArraySchema = z.array(unboundRecordSchema);
export type UnboundRecord = z.infer<typeof unboundRecordSchema>;

// ── Mutation schemas ─────────────────────────────────────────────────────

export const createClaimRegionSchema = z.object({
  claimRegion: z.string().min(1),
  sortOrder: z.number().int().optional(),
  isManagementGroup: z.boolean().optional(),
  isBackupHistory: z.boolean().optional(),
});

export const updateClaimRegionSchema = z.object({
  claimRegion: z.string().min(1).optional(),
  sortOrder: z.number().int().optional(),
  isManagementGroup: z.boolean().optional(),
  isBackupHistory: z.boolean().optional(),
});

export const createClaimTeamSchema = z.object({
  claimRegionTeam: z.string().min(1),
  manager: z.string().min(1),
  sortOrder: z.number().int().optional(),
  isClaimTeam: z.boolean().optional(),
});

export const updateClaimTeamSchema = z.object({
  claimRegionTeam: z.string().min(1).optional(),
  manager: z.string().min(1).optional(),
  sortOrder: z.number().int().optional(),
  isClaimTeam: z.boolean().optional(),
});

export const addClaimTeamUserSchema = z.object({
  userId: z.string().min(1),
  isExcludeFromPdf: z.boolean().optional(),
});

export const updateClaimTeamUserSchema = z.object({
  isExcludeFromPdf: z.boolean().optional(),
});

// Period names are month-year buckets in `YYYY-MM` form (e.g. "2026-04").
// Seed convention is monthly periods; admin-created periods must match.
// Trim before validating so accidental leading/trailing whitespace doesn't
// produce dropdown entries that look right but fail on string compare.
export const createPeriodSchema = z.object({
  name: z
    .string()
    .trim()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Period must be in YYYY-MM format (e.g. 2026-04)"),
});
